/**
 * Zerops Services - Shared Model Interfaces
 *
 * Unified interfaces for zagent, zcde, and shared service functionality.
 */

/**
 * Type aliases for zagent configuration
 */
export type ZagentAgentType = 'claude-code' | 'opencode-ai' | 'codex' | 'antigravity' | 'grok' | 'cursor';
export type ZagentAuthType = 'oauth' | 'token';

/**
 * Human-readable display names for each agent type. Exhaustive `Record`
 * so the compiler rejects adding a new agent without also adding its label,
 * replacing the silent "anything-but-claude becomes opencode.ai" fallback
 * that lived in two mini-card components.
 */
export const AGENT_DISPLAY_NAMES: Record<ZagentAgentType, string> = {
  'claude-code': 'Claude Code',
  'opencode-ai': 'opencode.ai',
  'codex': 'Codex',
  'antigravity': 'Antigravity',
  'grok': 'Grok Build',
  'cursor': 'Cursor CLI'
};

/**
 * UI-presentable agent types in canonical render order. opencode-ai is
 * defined in the type union for backend compatibility but isn't surfaced
 * in the UI today; consumers that iterate "the user-facing agents"
 * (mini-card quick-add, dialog rows, strip walker, the onboarding wizard's
 * static roster) should read from this list rather than
 * `Object.keys(AGENT_DISPLAY_NAMES)`.
 */
export const SUPPORTED_AGENT_TYPES: readonly ZagentAgentType[] = [
  'claude-code',
  'codex',
  'antigravity',
  'grok',
  'cursor'
] as const;

/**
 * Each agent has a single fixed provider (the company/API behind it).
 * Used to derive provider-driven values — env var keys for token mode,
 * `ZCP_PROVIDER_*` user-data entries, mini-card hints — without carrying
 * a per-agent `provider` field in form state.
 *
 * Exhaustive `Record` so adding an agent forces the provider to be picked.
 */
export const AGENT_PROVIDER_MAP: Record<ZagentAgentType, string> = {
  'claude-code': 'anthropic',
  'opencode-ai': 'anthropic',
  'codex': 'openai',
  'antigravity': 'google',
  'grok': 'xai',
  'cursor': 'cursor'
};

/**
 * Per-agent CLI command for the service-card's "Open in web terminal"
 * quick action. Each command launches the agent in its lowest-friction
 * mode — `--dangerously-skip-permissions` for Claude and the equivalent
 * `--dangerously-bypass-approvals-and-sandbox` for Codex, and `--force`
 * (Run Everything) for Cursor. Antigravity and Grok have no documented
 * skip-permissions flag yet, so they launch bare. Trailing `\n` so the
 * command is submitted to the shell on send.
 */
export const AGENT_LAUNCH_COMMANDS: Record<ZagentAgentType, string> = {
  'claude-code': 'claude --dangerously-skip-permissions\n',
  'opencode-ai': 'opencode\n',
  'codex': 'codex --dangerously-bypass-approvals-and-sandbox\n',
  'antigravity': 'agy\n',
  'grok': 'grok\n',
  'cursor': 'cursor-agent --force\n'
};

/**
 * Public access configuration (unified interface)
 * Used by both zagent and zcde service cards
 */
export interface ZeropsPublicAccess {
  mode: 'public' | 'vpn';
  url?: string;
  subdomain?: string;
  authEnabled?: boolean;
  password?: string;
}

/**
 * Access step configuration for shared step component
 * Used by popovers to display numbered steps with commands/links
 */
export interface AccessStepConfig {
  stepNumber: number;
  label: string;
  command?: string;
  copyable?: boolean;
  isLink?: boolean;
  linkUrl?: string;
  linkText?: string;
  note?: string;
}

/**
 * Service metadata extracted from user data — non-agent-specific bits only
 * (VS Code presence + SSHFS mount list). Agent state lives in
 * `ZeropsServiceMetadataMulti` and is read via `extractServiceMetadataMulti()`.
 */
export interface ZeropsServiceMetadata {
  hasVscode?: boolean;
  sshfsHostnames?: string[];
}

/**
 * Multi-agent metadata extracted from user data. Returned by
 * `extractServiceMetadataMulti()` — readers that need per-agent state
 * (service card rows, dual-agent display) consume this shape; everything
 * else stays on `ZeropsServiceMetadata`.
 *
 * Reads the unified `ZCP_AGENT_*_<SUFFIX>` per-agent env keys:
 *
 *   - `ZCP_AGENT_AUTH_TYPE_<SUFFIX>` → `authType` ('oauth' | 'token')
 *   - `ZCP_AGENT_OAUTH_<SUFFIX>`     → contributes to `authorized` (true ⇒ authed)
 *   - `ZCP_AGENT_TOKEN_<SUFFIX>`     → contributes to `authorized` (non-empty ⇒ authed)
 *
 * `authorized` is DERIVED — `OAUTH === 'true' || !!TOKEN`. There is no
 * standalone "authorized" env; the OAuth flag and the token presence
 * jointly express the auth state, and the extractor reconciles them so
 * callers don't have to.
 *
 * `agentConfigs` is populated for any agent that has at least ONE
 * per-agent env present — covers both deploy-time configuration and
 * post-deploy auth of an off-list agent. The legacy `ZCP_AGENT_TYPES`
 * index was dropped: it captured deploy-time intent and never updated,
 * so it drifted out of sync with the per-agent envs as soon as the user
 * touched anything post-deploy.
 */
export interface ZeropsServiceMetadataMulti {
  agentConfigs: Partial<Record<ZagentAgentType, {
    authType?: ZagentAuthType;
    authorized: boolean;
  }>>;
  hasVscode: boolean;
  sshfsHostnames: string[];
}

/**
 * One agent entry inside `ZagentMiniConfig.agents` — the per-agent shape
 * carried by the mini card and persisted alongside legacy single-agent fields.
 *
 * `apiToken` is only meaningful when `authType === 'token'`; carries the
 * token verbatim so a dialog reopen restores it.
 */
export interface ZagentMiniAgentEntry {
  agentType: ZagentAgentType;
  authType: ZagentAuthType;
  apiToken?: string;
}

/**
 * Mini configuration for zagent shown in recipe cards
 * Subset of full form values for quick configuration
 *
 * @deprecated `mode` and `includeVscode` retained for backwards compat with
 * already-saved configs. New code should use `enableAgent` + `enableVscode`.
 */
export interface ZagentMiniConfig {
  /** @deprecated use `enableAgent` instead */
  mode?: ControlPlaneMode;
  enableAgent?: boolean;
  enableVscode?: boolean;
  /**
   * Primary (first-enabled) agent. Mirrors `agents[0]` for back-compat with
   * single-agent consumers; the full enabled-agent list lives in `agents`.
   */
  agentType: ZagentAgentType;
  authType: ZagentAuthType;
  provider: string;
  apiToken?: string;
  /**
   * Full enabled-agent list. Populated by `extractZagentConfigFromForm` from
   * the multi-agent form fields. Empty / undefined for legacy single-agent
   * payloads (in which case mini-card consumers fall back to `agentType`).
   */
  agents?: ZagentMiniAgentEntry[];
  /** @deprecated use `enableVscode` instead */
  includeVscode: boolean;
  enablePublicAccess: boolean;
  vscodePassword?: string;
  selectedServices: string[];  // Service hostnames
}

/**
 * Service category for filtering connectable services
 * Only USER services can be connected via SSHFS
 */
export type ZagentServiceCategory = 'USER' | 'STANDARD' | 'OBJECT_STORAGE' | 'LOCAL_STORAGE' | 'CORE';

/**
 * Context information when configuring zagent from a recipe
 */
export interface ZagentRecipeContext {
  services: Array<{ name: string; typeName: string; category: ZagentServiceCategory }>;
  environmentName: string;
  projectName: string;
}

/**
 * Mode for the AI agent dialog
 */
export type ZagentDialogMode = 'standalone' | 'recipe';

/**
 * Control Plane mode - AI Agent or Human (Cloud IDE)
 */
export type ControlPlaneMode = 'ai-agent' | 'human';

/**
 * Unified configuration for Control Plane mini card
 *
 * Two independent opt-in flags drive the deployment shape:
 * - `enableAgent`: install + bootstrap the Coding Agent CLI
 * - `enableVscode`: install + start the Cloud IDE (browser VS Code)
 *
 * @deprecated `mode` and `includeVscode` retained for backwards compat with
 * already-saved configs. New code should use `enableAgent` + `enableVscode`.
 */
export interface ControlPlaneMiniConfig {
  /** @deprecated use `enableAgent` instead */
  mode?: ControlPlaneMode;
  enableAgent?: boolean;
  enableVscode?: boolean;
  // Agent-only fields (used when enableAgent === true)
  agentType?: ZagentAgentType;
  authType?: ZagentAuthType;
  provider?: string;
  apiToken?: string;
  /**
   * Full enabled-agent list. Mirrors `ZagentMiniConfig.agents` for the
   * dialog-reopen path so multi-agent picks survive a round-trip.
   */
  agents?: ZagentMiniAgentEntry[];
  // Common
  /** @deprecated use `enableVscode` instead */
  includeVscode: boolean;
  enablePublicAccess: boolean;
  vscodePassword?: string;
  selectedServices: string[];
}

/**
 * Default configuration for Control Plane mini card
 */
export const DEFAULT_CONTROL_PLANE_MINI_CONFIG: ControlPlaneMiniConfig = {
  enableAgent: true,
  enableVscode: true,
  agentType: 'claude-code',
  authType: 'oauth',
  includeVscode: true,
  enablePublicAccess: true,
  selectedServices: []
};
